import {
    Badge,
    Tooltip,
} from '@mantine/core';

import {
    TestObservation,
    VerificationResult,
} from '../types';
import {
    classifyObservation,
    ObservationKind,
    observationKindDisplay,
} from '../utils/classifyObservation';

// The outcome badge shows the CLASSIFIED kind, not the raw `outcome` enum. Rendering
// "SKIPPED" three different times for three different meanings is exactly the
// ambiguity this module exists to remove — see classifyObservation.
export const OutcomeBadge = ({
                                 record,
                                 kind,
                             }: {
    record: Pick<TestObservation, 'outcome' | 'error' | 'verificationResult' | 'dependedOn'>,
    kind?: ObservationKind,
}) => {
    const resolved = kind ?? classifyObservation(record);
    const display = observationKindDisplay(resolved);

    return (
        <Tooltip label={display.description} multiline w={280} withArrow>
            <Badge
                color={display.color}
                variant={display.muted ? 'light' : 'filled'}
            >
                {display.label}
            </Badge>
        </Tooltip>
    );
};

// verificationResult gets EQUAL BILLING with outcome (FNP-861) — it is a first-class
// column, never something you have to open a detail pane to discover. NOT_VERIFIED
// is the valuable one: the interaction worked, the platform did not record it.
const VERIFICATION_DISPLAY: Record<VerificationResult, { color: string, label: string, description: string }> = {
    [VerificationResult.VERIFIED]: {
        color: 'green',
        label: 'Verified',
        description: 'The platform recorded the effect of this action.',
    },
    [VerificationResult.NOT_VERIFIED]: {
        color: 'orange',
        label: 'Not verified',
        description: 'The platform did NOT record the effect of this action. On a passing action this is the strongest signal the fleet produces.',
    },
    [VerificationResult.NOT_APPLICABLE]: {
        color: 'gray',
        label: 'n/a',
        description: 'This action has nothing to verify.',
    },
};

export const VerificationBadge = ({verificationResult}: { verificationResult?: VerificationResult | null }) => {
    if (!verificationResult) {
        // Distinct from NOT_APPLICABLE: the executor recorded no verification at all.
        return <Badge color={'gray'} variant={'outline'}>—</Badge>;
    }

    const display = VERIFICATION_DISPLAY[verificationResult];
    if (!display) {
        return <Badge color={'dark'}>{verificationResult}</Badge>;
    }

    return (
        <Tooltip label={display.description} multiline w={280} withArrow>
            <Badge
                color={display.color}
                variant={verificationResult === VerificationResult.NOT_VERIFIED ? 'filled' : 'light'}
            >
                {display.label}
            </Badge>
        </Tooltip>
    );
};
